import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/hooks/use-toast"
import { useAuthContext } from "@/contexts/AuthContext"
import { useTournamentTypesList } from "@/graphql/hooks/useTournamentTypesList"
import { useAllFormats } from "@/graphql/hooks/useAllFormats"
import { useTournamentRegister } from "@/graphql/hooks/useTournamentRegister"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useStoreDetail } from "@/graphql/hooks/useStoreDetail"

const formSchema = z.object({
  name: z.string().min(10, "El nombre debe tener al menos 10 caracteres"),
  description: z.string().min(20, "La descripción debe tener al menos 20 caracteres"),
  gameId: z.string().min(1, "Selecciona un juego"),
  typeId: z.string().min(1, "Selecciona un tipo de torneo"),
  formatId: z.string().min(1, "Selecciona un formato"),
  startDate: z.string().min(1, "Selecciona una fecha"),
  validDates: z.boolean(),
})

type FormData = z.infer<typeof formSchema>

export default function CreateTournament() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { userInfo } = useAuthContext()
  const [selectedGameId, setSelectedGameId] = useState<number | null>(null)

  const [allowDoubleDate, setAllowDoubleDate] = useState(false)

  // Assuming storeId is 1 for now - this should come from user context
  const storeId = userInfo?.User?.Person?.Store.id || 0
  const skipStoreDetail = !storeId
  const { store, loading: storeLoading } = useStoreDetail(storeId, {
    skip: skipStoreDetail,
  })
  const { tournamentTypes, loading: typesLoading } = useTournamentTypesList({
    storeId,
  })
  const { formats, loading: formatsLoading } = useAllFormats()
  const { registerTournament, loading: registerLoading } = useTournamentRegister()

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      gameId: "",
      typeId: "",
      formatId: "",
      startDate: "",
      validDates: false,
    },
  })

  // Filter formats based on selected game
  const availableFormats = selectedGameId
    ? formats.filter((format) => format.FormatGames.some((fg) => fg.gameId === selectedGameId))
    : []

  // Reset format when game changes
  useEffect(() => {
    if (selectedGameId) {
      form.setValue("formatId", "")
    }
  }, [selectedGameId, form])

  const onSubmit = async (data: FormData) => {
    try {
      // Check if user is authenticated (for now, we'll use storeId = 1 as default)
      if (!userInfo?.User) {
        toast({
          title: "Error",
          description: "Debes estar autenticado para crear torneos",
          variant: "destructive",
        })
        return
      }

      const input = {
        name: data.name,
        description: data.description,
        image: "",
        eventUrl: "",
        addressId: store?.Address?.id || 0, // This should be dynamic based on store
        gameId: parseInt(data.gameId),
        typeId: parseInt(data.typeId),
        formatId: parseInt(data.formatId),
        startDate: `${data.startDate} 00:00`,
        validDates: data.validDates ? 2 : 1,
      }

      const result = await registerTournament(input)

      if (result?.status?.success) {
        toast({
          title: "Torneo creado",
          description: "Ahora puedes gestionar a los participantes e iniciar el evento.",
          variant: "success",
        })
        navigate(result.tournament.urlView)
      } else {
        toast({
          title: "Error",
          description: result?.status?.detail || "Error al crear el torneo",
          variant: "destructive",
        })
      }
    } catch {
      toast({
        title: "Error",
        description: "Error al crear el torneo",
        variant: "destructive",
      })
    }
  }

  /**
   * Valida que la tienda esté activa.
   */
  useEffect(() => {
    if (!storeLoading && store && !store?.active) {
      toast({
        title: "Error",
        description: "La tienda aún no está activa para crear torneos.",
        variant: "destructive",
      })
      navigate(store?.urlView || "/tournaments")
    }
  }, [store, storeLoading, navigate, toast])

  if (typesLoading || formatsLoading || storeLoading) {
    return (
      <Layout>
        <LoadingScreen message="Cargando formulario..." />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="container mx-auto py-8">
        <Card className="max-w-2xl mx-auto bg-card dark:bg-card-dark">
          <CardHeader>
            <CardTitle className="dark:text-gray-300">
              Crear Nuevo Torneo para <i>{store?.name}</i>
            </CardTitle>
            <CardDescription className="dark:text-gray-400">
              Completa todos los campos para crear un nuevo torneo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Nombre */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="dark:text-gray-300">Nombre del Torneo</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ingresa el nombre del torneo"
                          className="dark:bg-input-dark dark:text-gray-300 placeholder:text-gray-400"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Descripción */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="dark:text-gray-300">Descripción</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe el torneo"
                          className="min-h-[100px] dark:bg-input-dark dark:text-gray-300 placeholder:text-gray-400"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Formato / Tipo / Juego */}
                {["gameId", "typeId", "formatId"].map((name) => (
                  <FormField
                    key={name}
                    control={form.control}
                    name={name as any}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="dark:text-gray-300">
                          {name === "gameId"
                            ? "Formato"
                            : name === "typeId"
                              ? "Tipo de Torneo"
                              : "Modalidad de juego"}
                        </FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value)
                            if (name === "gameId") setSelectedGameId(parseInt(value))
                            if (name === "typeId") {
                              const selectedType = tournamentTypes.find(
                                (type) => type.id.toString() === value,
                              )
                              if (selectedType) {
                                setAllowDoubleDate(selectedType.canBeDouble === 1)
                                if (!selectedType.canBeDouble) form.setValue("validDates", false)
                              }
                            }
                          }}
                          defaultValue={field.value}
                          disabled={name === "formatId" && !selectedGameId}
                        >
                          <FormControl>
                            <SelectTrigger className="dark:bg-input-dark dark:text-gray-300">
                              <SelectValue
                                placeholder={
                                  name === "gameId"
                                    ? "Selecciona un formato"
                                    : name === "typeId"
                                      ? "Selecciona un tipo de torneo"
                                      : "Selecciona una modalidad de juego"
                                }
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="dark:bg-card-dark dark:text-gray-300">
                            {name === "gameId" &&
                              store?.StoreGames.map((game) => (
                                <SelectItem
                                  key={game.gameId}
                                  value={game.gameId.toString()}
                                  className="dark:text-gray-300"
                                >
                                  {game.Game.name}
                                </SelectItem>
                              ))}
                            {name === "typeId" &&
                              tournamentTypes.map((type) => (
                                <SelectItem
                                  key={type.id}
                                  value={type.id.toString()}
                                  className="dark:text-gray-300"
                                >
                                  {type.name}
                                </SelectItem>
                              ))}
                            {name === "formatId" &&
                              availableFormats.map((format) => (
                                <SelectItem
                                  key={format.id}
                                  value={format.id.toString()}
                                  className="dark:text-gray-300"
                                >
                                  {format.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {name === "formatId" && !selectedGameId && (
                          <FormDescription className="dark:text-gray-400">
                            Primero selecciona un juego
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}

                {/* Fecha */}
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="dark:text-gray-300">Fecha del evento</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          className="dark:bg-input-dark dark:text-gray-300 placeholder:text-gray-400"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Fecha Doble */}
                {allowDoubleDate && (
                  <FormField
                    control={form.control}
                    name="validDates"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 dark:border-gray-700">
                        <div className="space-y-0.5">
                          <FormLabel className="dark:text-gray-300 text-base">
                            Fecha Doble
                          </FormLabel>
                          <FormDescription className="dark:text-gray-400">
                            ¿El torneo se realizará válido por más de una fecha?
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}

                {/* Botones */}
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1" disabled={registerLoading}>
                    {registerLoading ? "Creando..." : "Crear Torneo"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
